import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../UI/Button';

export function useDirtyBaseline<T>(value: T, isReady: boolean) {
  const [baseline, setBaseline] = useState<string | null>(null);
  const signature = JSON.stringify(value);

  useEffect(() => {
    if (!isReady) {
      setBaseline(null);
      return;
    }
    setBaseline((current) => current ?? signature);
  }, [isReady, signature]);

  const markClean = () => setBaseline(JSON.stringify(value));
  const isDirty = isReady && baseline !== null && signature !== baseline;
  const previous = baseline ? JSON.parse(baseline) as T : null;
  return { isDirty, markClean, previous };
}

export function useUnsavedChangesGuard(isDirty: boolean) {
  const navigate = useNavigate();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const pendingHref = useRef<string | null>(null);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = (event.target as HTMLElement | null)?.closest('a[href]') as HTMLAnchorElement | null;
      if (!target || target.target === '_blank' || target.hasAttribute('download')) return;
      const href = target.getAttribute('href');
      if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) return;
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      event.preventDefault();
      event.stopPropagation();
      pendingHref.current = `${url.pathname}${url.search}${url.hash}`;
      setLeaveOpen(true);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty]);

  const stay = () => {
    pendingHref.current = null;
    setLeaveOpen(false);
  };

  const leaveWithoutSaving = () => {
    const href = pendingHref.current;
    pendingHref.current = null;
    setLeaveOpen(false);
    if (href) navigate(href);
  };

  return { leaveOpen, stay, leaveWithoutSaving };
}

export const UnsavedLeaveDialog: React.FC<{
  open: boolean;
  onStay: () => void;
  onLeave: () => void;
}> = ({ open, onStay, onLeave }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900">Save before you leave?</h2>
        <p className="mt-2 text-sm text-gray-600">You have unsaved changes. Save them first, or leave without saving.</p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onStay}>Stay</Button>
          <Button type="button" onClick={onLeave}>Leave without saving</Button>
        </div>
      </div>
    </div>
  );
};

export const ConfirmChangesDialog: React.FC<{
  open: boolean;
  title: string;
  lines: string[];
  confirmLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, title, lines, confirmLabel, busy, onCancel, onConfirm }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-2 text-sm text-gray-600">Review these changes, then confirm.</p>
        <ul className="mt-4 max-h-64 overflow-y-auto space-y-1 text-sm text-gray-800 list-disc pl-5">
          {lines.map((line, index) => <li key={`${index}-${line}`}>{line}</li>)}
        </ul>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button type="button" onClick={onConfirm} disabled={busy}>{busy ? 'Saving…' : confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
};
